// Cross-capability disambiguation — the router seam BETWEEN Otto's capabilities.
//
// Several "container nouns" (Regions, Sites, Buildings, Additions, Floors, Recommendations) mean different
// things in different capabilities: each is at once a FILTER dimension, a CHART grouping, AND a project-level
// NAVIGATION page. So a bare "show me regions" is genuinely ambiguous — filter by region? group a chart by
// region? open the project's Regions page? The server classifier is single-label and must pick ONE; when the
// user gave no capability-specific verb, that guess is a coin-flip.
//
// This module CONFIRMS intent instead of guessing. Like every other availability/ambiguity decision in Otto
// (the Stage-A routing demotion, the charts availability logic), it lives in DETERMINISTIC frontend code +
// data, NOT a prompt — the frontend is the only layer that holds all three capabilities' real option sets
// (filter `availableFields`, chart `app_context`, nav `navTargets`). We offer ONLY the capabilities that are
// actually viable for the current scope, and only clarify when two or more are.
import { normalizeText } from "../../core/normalizeText";
import { entityTerms } from "../planner/pageManifest";
import { rankPageTargets, rankAndCap } from "../planner/pageRanking";

// The ambiguous container nouns — DERIVED from the page registry, not hand-listed. `chartValue` is the
// grouping option value the charts use (null when the noun isn't a chart grouping); `navKeys` are the
// nav-catalog target keys that represent it. Viability is still tested against the LIVE inputs below — this
// registry only names the nouns and their canonical forms.
//
// It used to be a literal, which meant every new page needed a SECOND edit here before Otto would stop
// guessing at its noun — and a page whose noun nobody remembered to add (assets, images) silently routed to
// the filter path forever. Now registering a page with an `entity` in pageManifest.js is the whole change:
// the noun becomes disambiguating everywhere at once, and two pages that share a noun (the project hub and
// the asset hub both own "buildings") collapse into ONE term carrying both navKeys, so the user is asked
// about the noun once rather than once per page.
export const CROSS_CAPABILITY_TERMS = entityTerms();

// Leading filler that carries NO capability signal — stripped before we test whether what remains is just a
// bare container noun. ("show me the regions" -> "regions".) Kept generic (view/see/show/display), NOT
// capability verbs — those are the tell that the user already chose a capability (see CAPABILITY_VERBS).
const LEADING_FILLER = [
    "please", "can you", "could you", "would you", "i want to", "i want", "i would like to", "i'd like to",
    "let me", "let me see", "show me", "show", "see", "view", "display", "give me", "get me", "pull up",
    "bring up", "the", "all", "all the", "my", "our", "a", "an", "list of", "list"
];

// A capability-specific verb/keyword means the user ALREADY named an intent — don't second-guess it, let the
// normal routing (classifier -> capability) proceed. Only a BARE noun with none of these is disambiguated.
// Matched on WHOLE-word / whole-phrase boundaries (see hasCapabilityVerb), so "only" never fires inside
// "commonly" and "top" never inside "laptop".
const CAPABILITY_VERBS = [
    // filter
    "filter", "filters", "filtered", "filtering", "narrow", "refine", "exclude", "only", "just the",
    "where", "with a", "that are", "that have", "having",
    // navigate
    "go to", "goto", "navigate", "open", "take me", "bring me", "jump", "visit", "page", "section", "screen",
    // charts
    "chart", "charts", "graph", "plot", "group by", "grouped by", "group the", "break down", "breakdown",
    "pie", "bar", "column", "donut", "treemap", "bubble", "sort", "top"
];

// True when the (normalized) utterance contains any capability verb as a whole word/phrase.
const hasCapabilityVerb = norm => {
    const padded = ` ${norm} `;
    return CAPABILITY_VERBS.some(v => padded.includes(` ${v} `));
};

// Strip leading filler tokens iteratively until what's left starts with a non-filler word. Longest phrases
// first so "show me the" collapses fully. Returns the trimmed remainder.
const stripLeadingFiller = phrase => {
    let out = ` ${phrase} `;
    let changed = true;
    const ordered = [...LEADING_FILLER].sort((a, b) => b.length - a.length);
    while (changed) {
        changed = false;
        for (const f of ordered) {
            const prefix = ` ${f} `;
            if (out.startsWith(prefix)) {
                out = ` ${out.slice(prefix.length)}`;
                changed = true;
                break;
            }
        }
    }
    return out.trim();
};

// Index singular + plural -> canonical term id, so "region"/"regions" both resolve.
const TERM_BY_WORD = (() => {
    const idx = {};
    Object.entries(CROSS_CAPABILITY_TERMS).forEach(([id, t]) => {
        idx[t.singular] = id;
        idx[t.plural] = id;
    });
    return idx;
})();

// Does the utterance name a bare container noun (with no capability verb)? Returns the canonical term id or
// null. The remainder after stripping filler must be EXACTLY the noun (singular/plural) — "regions", not
// "regions by cost" (which carries its own signal and routes normally).
const bareTermOf = utterance => {
    const norm = normalizeText(utterance);
    if (!norm) return null;
    if (hasCapabilityVerb(norm)) return null;
    const remainder = stripLeadingFiller(norm);
    return TERM_BY_WORD[remainder] || null;
};

// The STRENGTH of a term's FILTER match, so the card can tell a real dimension from the dataset's own name:
//   "exact" — a dimension whose LABEL IS the noun (region -> the "Region" field). "filter by region" is a
//             genuine directive: region is a filterable attribute with its own values.
//   "loose" — the noun appears only as a TOKEN inside a longer dimension label ("recommendation" inside
//             "Recommendation Type"). Here the noun names the ENTITY / whole dataset, not a filter value, so a
//             literal "filter by recommendation" is a category error — it just searches for the text. The card
//             still OFFERS a filter choice, but its pick clarifies WHAT to filter by (see the proposal builder).
//   null    — no dimension carries the noun at all.
const filterMatchMode = (term, availableFields) => {
    const words = new Set([term.singular, term.plural]);
    let loose = false;
    for (const f of availableFields || []) {
        if (f && f.kind && f.kind !== "dimension") continue; // wildcards/scalars aren't the noun's dimension
        const label = normalizeText(f && (f.label || f.key));
        if (!label) continue;
        if (words.has(label)) return "exact"; // the label IS the noun -> a real filter dimension
        if (label.split(" ").some(tok => words.has(tok))) loose = true; // noun only a token inside a longer label
    }
    return loose ? "loose" : null;
};

// Is `term` filterable at all in this scope (exact OR loose)? Viability still offers the filter choice for a
// loose/entity match; only the exact case keeps the direct "filter by <noun>" directive.
const filterViable = (term, availableFields) => filterMatchMode(term, availableFields) !== null;

// Is `term` a real CHART grouping in this scope? Its grouping value appears in ANY chart's option lists
// (category/display/…). Scans defensively so it follows whatever grouping properties the dashboard sends.
const chartViable = (term, chartContext) => {
    if (!term.chartValue) return false;
    const charts = (chartContext && chartContext.charts) || {};
    return Object.keys(charts).some(chart => {
        const options = (charts[chart] && charts[chart].options) || {};
        return Object.keys(options).some(prop =>
            (options[prop] || []).some(o => o && o.value === term.chartValue)
        );
    });
};

// The reachable NAVIGATION target for `term` in this scope (or null). A nav target keyed for the noun (or
// whose label/synonyms name it). navTargets is already permission-scoped, so a page the user can't reach
// isn't here. Returned (not just a boolean) so the choice card can navigate DIRECTLY on click — picking
// "Go to the … page" IS the confirmation, so it shouldn't produce a second nav-confirm card.
// EVERY reachable destination a term names, best-first. A derived term routinely carries several navKeys —
// "buildings" belongs to both the project hub and the asset hub; "assets" to both the Asset Dashboard and the
// Asset Management register — and collapsing that to one destination is a real loss: asked from the FCA
// Dashboard, "show me my assets" was offering only the register, silently dropping the Asset Dashboard, which
// is the closer analogue of where the user is standing.
//
// Ranked (family, then tier, then catalog order) and CAPPED, so the card leads with the right one for this
// mount and can never grow into a directory as more pages register the noun.
const navTargetsFor = (term, navTargets, page) => {
    const keys = new Set(term.navKeys);
    const words = new Set([term.singular, term.plural]);
    const matches = (navTargets || []).filter(t => {
        if (!t) return false;
        if (keys.has(t.key)) return true;
        const names = [normalizeText(t.label), ...((t.synonyms || []).map(normalizeText))];
        return names.some(n => words.has(n));
    });
    if (matches.length < 2) return matches.slice(0, 1);
    // Two destinations plus the filter/chart choices is already a full card, so cap tighter than the
    // three-page default: the point is to stop hiding a viable destination, not to list them all.
    return rankAndCap(matches, { page, max: 2 }).shown;
};

const navTargetFor = (term, navTargets, page) => navTargetsFor(term, navTargets, page)[0] || null;
const navViable = (term, navTargets) => !!navTargetFor(term, navTargets);

// The capability options, in a stable order (filter, chart, navigate) — the order the choice card renders.
const VIABILITY = [
    { capability: "filter", viable: filterViable, ctxKey: "availableFields" },
    { capability: "chart", viable: chartViable, ctxKey: "chartContext" },
    { capability: "navigate", viable: navViable, ctxKey: "navTargets" }
];

// Detect a request whose bare container noun Otto should CONFIRM rather than guess at. Returns null when the
// utterance isn't a bare container noun, when a capability verb is present, or when nothing needs confirming.
// Otherwise returns { term: <id>, singular, plural, capabilities: ["filter","chart","navigate"] }.
//
// Two viable capabilities or more is the classic ambiguity: the server classifier had to pick one and we ask
// instead. A LONE viable capability normally needs no offer — normal routing lands it — with one exception
// that matters more as the registry grows: a lone NAVIGATE. The classifier's documented doubt-default is
// filter_query, so a noun that is ONLY a page ("show me my assets" on the recommendations dashboard, where
// nothing is filterable by asset) is systematically routed to a filter that cannot succeed, and the reachable
// page is never mentioned. Most pages we register from here on own a noun the dashboard cannot filter by, so
// without this the registry would grow while Otto stayed silent about it. Skipped when the classifier already
// said `navigate` — that route grounds the phrase properly on its own and re-asking here would duplicate it.
export const detectCrossCapabilityAmbiguity = (
    utterance,
    { availableFields, chartContext, navTargets, routedKind, page } = {}
) => {
    const id = bareTermOf(utterance);
    if (!id) return null;
    const term = CROSS_CAPABILITY_TERMS[id];
    if (!term) return null;
    const ctx = { availableFields, chartContext, navTargets };
    const capabilities = VIABILITY.filter(v => v.viable(term, ctx[v.ctxKey])).map(v => v.capability);
    const navOnly = capabilities.length === 1 && capabilities[0] === "navigate";
    if (capabilities.length < 2 && !(navOnly && routedKind !== "navigate")) return null;
    const result = { term: id, singular: term.singular, plural: term.plural, capabilities, navOnly };
    // How the filter choice should behave: an EXACT dimension match keeps its direct "filter by <noun>"
    // directive; an entity noun (LOOSE match, e.g. "recommendations") clarifies what to filter by instead.
    if (capabilities.includes("filter")) result.filterMode = filterMatchMode(term, availableFields);
    // Carry the actual nav target so picking "Go to the … page" navigates immediately (that click IS the
    // confirmation) instead of re-submitting a directive that asks to confirm the navigation again.
    if (capabilities.includes("navigate")) {
        const targets = navTargetsFor(term, navTargets, page);
        // `navTarget` stays the single best destination (every existing consumer + the span reads it);
        // `navTargets` carries the rest so the card can offer them.
        if (targets.length) {
            result.navTarget = targets[0];
            result.navTargets = targets;
        }
    }
    return result;
};
